import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchResults } from '@/features/ticket-admin/SearchResults'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { extractErrorMessage } from '@/queries/ticketMutations'
import { useCompanySearch } from '@/queries/people'
import { useSlaSettings, useStaffSettings } from '@/queries/settings'
import { formatBusinessHours } from '@/lib/sla'
import { formatDate } from '@/lib/dates'
import type { CompanySla, SlaSettingsRow } from '@/types/sla'
import { SlaEditor, type Editing } from './SlaEditor'

function AddCompany({ listed, onPick }: { listed: Set<string>; onPick: (company: { _id: string; name?: string }) => void }) {
  const [query, setQuery] = useState('')
  const debounced = useDebouncedValue(query)
  const companies = useCompanySearch(debounced)
  const unlisted = companies.data?.filter(c => !listed.has(c._id))

  return (
    <div className="max-w-sm space-y-1">
      <Label htmlFor="sla-add-company">Add company</Label>
      <Input id="sla-add-company" autoComplete="off" placeholder="Search companies" value={query} onChange={e => setQuery(e.target.value)} />
      <SearchResults
        label="Matching companies"
        term={query}
        items={unlisted}
        isFetching={companies.isFetching || query !== debounced}
        onPick={c => {
          setQuery('')
          onPick(c)
        }}
        renderItem={c => <span className="text-etn-text-cell">{c.name || c._id}</span>}
      />
    </div>
  )
}

function SlaSettingsList({ defaults, rows }: { defaults: CompanySla; rows: SlaSettingsRow[] }) {
  const [editing, setEditing] = useState<Editing | null>(null)
  // Every admin, not just the team, so a contact who has since left it is still named.
  const staff = useStaffSettings()
  const names = new Map((staff.data?.admins ?? []).map(a => [a._id, a.name ?? a.email ?? a._id]))

  return (
    <div className="space-y-4">
      <AddCompany listed={new Set(rows.map(r => r.companyId))} onPick={c => setEditing({ companyId: c._id, name: c.name ?? c._id, sla: defaults })} />

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No company has SLAs set up yet.</p>
      ) : (
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-border text-xs text-muted-foreground">
              <th scope="col" className="pb-2 pr-4 font-normal">
                Company
              </th>
              <th scope="col" className="pb-2 pr-4 font-normal">
                SLAs
              </th>
              <th scope="col" className="pb-2 pr-4 font-normal">
                Primary contact
              </th>
              <th scope="col" className="pb-2 pr-4 font-normal">
                Hours
              </th>
              <th scope="col" className="pb-2 pr-4 font-normal">
                Ends
              </th>
              <th scope="col" className="pb-2 font-normal">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.companyId} className="border-b border-border last:border-0">
                <td className="py-2 pr-4 text-sm text-foreground">{row.name}</td>
                <td className="py-2 pr-4">
                  <Badge variant={row.sla.enabled ? 'success' : 'neutral'}>{row.sla.enabled ? 'On' : 'Off'}</Badge>
                </td>
                <td className="py-2 pr-4 text-sm text-foreground">
                  {row.sla.primaryContactId ? (names.get(row.sla.primaryContactId) ?? '…') : <span className="text-muted-foreground">None</span>}
                </td>
                <td className="py-2 pr-4 text-xs text-muted-foreground">{formatBusinessHours(row.sla.businessHours)}</td>
                <td className="py-2 pr-4 text-sm text-foreground">
                  {row.sla.endDate ? formatDate(row.sla.endDate) : <span className="text-muted-foreground">Not set</span>}
                </td>
                <td className="py-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    aria-label={`Edit ${row.name}`}
                    onClick={() => setEditing({ companyId: row.companyId, name: row.name, sla: row.sla })}
                  >
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={editing !== null} onOpenChange={open => !open && setEditing(null)}>
        {/* Keyed so each opening starts from that company's saved settings, not the last draft. */}
        {editing && <SlaEditor key={editing.companyId} editing={editing} onClose={() => setEditing(null)} />}
      </Dialog>
    </div>
  )
}

export function SlaSettingsView() {
  const { data, isLoading, error } = useSlaSettings()

  return (
    <div className="container mx-auto max-w-4xl px-6 pb-6">
      <Card>
        <CardHeader>
          <CardTitle>SLAs</CardTitle>
          <p className="text-sm text-muted-foreground">
            SLAs are off for every company until switched on here. Alerts post to #client-support and tag the company&apos;s primary contact, who
            needs a Slack member ID on the Support team card to be tagged rather than named.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {error && <p className="text-sm text-destructive">{extractErrorMessage(error, 'Could not load the SLA settings')}</p>}
          {data && <SlaSettingsList defaults={data.defaults} rows={data.data} />}
        </CardContent>
      </Card>
    </div>
  )
}
